import L from 'leaflet';

/**
 * Screen-space positions for spiderfying a cluster's members around its centre.
 *
 * Same shape language as Leaflet.markercluster: a ring for small groups, an
 * Archimedean spiral once a ring would put the feet too close together. Laying
 * the feet out in pixels (rather than degrees) keeps the spacing constant at
 * every zoom, which is the whole point — a cluster of dealers sharing one ZIP
 * centroid has no geographic spread to zoom into.
 */

const CIRCLE_MAX = 9;
const CIRCLE_FOOT_SEPARATION = 25;
const CIRCLE_START_ANGLE = Math.PI / 6;
const CIRCLE_MIN_RADIUS = 35;
const SPIRAL_FOOT_SEPARATION = 28;
const SPIRAL_LENGTH_START = 11;
const SPIRAL_LENGTH_FACTOR = 5;

function circlePoints(count: number, centre: L.Point): L.Point[] {
  const circumference = CIRCLE_FOOT_SEPARATION * (2 + count);
  const radius = Math.max(circumference / (2 * Math.PI), CIRCLE_MIN_RADIUS);
  const angleStep = (2 * Math.PI) / count;
  const res: L.Point[] = [];
  for (let i = 0; i < count; i++) {
    const angle = CIRCLE_START_ANGLE + i * angleStep;
    res.push(L.point(centre.x + radius * Math.cos(angle), centre.y + radius * Math.sin(angle)));
  }
  return res;
}

function spiralPoints(count: number, centre: L.Point): L.Point[] {
  const lengthFactor = SPIRAL_LENGTH_FACTOR * 2 * Math.PI;
  let legLength = SPIRAL_LENGTH_START;
  let angle = 0;
  const res: L.Point[] = new Array(count);
  // Filled back-to-front: the leg grows as the spiral unwinds, so the LAST
  // member lands nearest the centre and the first sits furthest out.
  for (let i = count; i >= 0; i--) {
    if (i < count) {
      res[i] = L.point(centre.x + legLength * Math.cos(angle), centre.y + legLength * Math.sin(angle));
    }
    angle += SPIRAL_FOOT_SEPARATION / legLength + i * 0.0005;
    legLength += lengthFactor / angle;
  }
  return res;
}

/** One lat/lng per member, laid out around `centre` at the map's current zoom. */
export function spiderfyPositions(map: L.Map, centre: L.LatLng, count: number): L.LatLng[] {
  if (count <= 0) return [];
  const centrePt = map.latLngToLayerPoint(centre);
  const pts = count <= CIRCLE_MAX ? circlePoints(count, centrePt) : spiralPoints(count, centrePt);
  return pts.map((p) => map.layerPointToLatLng(p));
}
